import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MessageCircle, LayoutDashboard, LogOut, User, ShoppingBasket, Compass, Bell } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SocialLinks } from "@/components/SocialLinks";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [unreadNotif, setUnreadNotif] = useState(0);

  useEffect(() => {
    if (!user || !user.role) return;
    const load = () => {
      api.get("/messages/unread").then(({ data }) => setUnread(data.total)).catch(() => {});
      api.get("/notifications/unread-count").then(({ data }) => setUnreadNotif(data.count)).catch(() => {});
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = user && user.name
    ? user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <header className="sticky top-0 z-50 glass border-b border-border" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo">
          <Logo size={40} className="ring-1 ring-border" />
          <span className="hidden sm:inline font-serif text-xl md:text-2xl leading-none text-olive">
            L’Orne <span className="text-rose-brand">Park</span>
          </span>
        </Link>

        <nav className="flex items-center gap-2 md:gap-3">
          <Link to="/" className="hidden md:inline-block text-sm font-medium text-muted-foreground hover:text-olive transition-colors px-3" data-testid="nav-explore">
            Explorer
          </Link>
          <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-olive transition-colors px-2 md:px-3" data-testid="nav-shop">
            <ShoppingBasket className="w-4 h-4" /> <span className="hidden md:inline">Course en ligne</span>
          </Link>
          <Link to="/region" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-olive transition-colors px-2 md:px-3" data-testid="nav-region">
            <Compass className="w-4 h-4" /> <span className="hidden md:inline">La région</span>
          </Link>
          <SocialLinks className="hidden lg:flex" />
          {user && user.role && (
            <Link to="/mes-notifications" data-testid="nav-notifications">
              <Button variant="ghost" size="sm" className="relative text-muted-foreground hover:text-olive px-2">
                <Bell className="w-4 h-4" />
                {unreadNotif > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-brand text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{unreadNotif}</span>
                )}
              </Button>
            </Link>
          )}
          {user && user.role && (
            <Link to="/messagerie" data-testid="nav-messages">
              <Button variant="ghost" size="sm" className="relative text-muted-foreground hover:text-olive">
                <MessageCircle className="w-4 h-4 md:mr-1.5" />
                <span className="hidden md:inline">Messagerie</span>
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-brand text-white text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">
                    {unread}
                  </span>
                )}
              </Button>
            </Link>
          )}
          {user && user.role ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-border bg-white pl-3 pr-1.5 py-1 hover:shadow-md transition-shadow" data-testid="user-menu-trigger">
                  <span className="hidden sm:block text-xs font-semibold text-olive capitalize">{user.role}</span>
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-rosebrand-pale text-rose-brand text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-semibold text-olive">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.phone_only ? user.phone : user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/mes-sejours")} data-testid="menu-my-trips">
                  <User className="w-4 h-4 mr-2" /> Mes séjours
                </DropdownMenuItem>
                {(user.role === "host" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/espace-hote")} data-testid="menu-host">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Espace Hôte / Équipe
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/administration")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Administration
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/connexion" data-testid="nav-login">
                <Button variant="ghost" size="sm" className="text-olive">Connexion</Button>
              </Link>
              <Link to="/inscription" data-testid="nav-register">
                <Button size="sm" className="bg-rose-brand hover:bg-rosebrand-soft text-white rounded-full px-3 sm:px-5">
                  <span className="sm:hidden">Inscription</span><span className="hidden sm:inline">Devenir membre</span>
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
